import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { BsSend } from "react-icons/bs";
import { IoClose } from "react-icons/io5";
import Styles from "./Chatbot.module.scss";
import robot from "../../assets/logo/robot-ass.svg";
import api from "../../api";
import LoadingText from "./LoadingText";
import TypingMessage from "./TypingMessage";
import { trackEvent } from "../../utils/gaTracking";

const Chatbot = () => {
    const [show, setShow] = useState(false);
    const [query, setQuery] = useState("");
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [faqs, setFaqs] = useState([]);
    const [showFaqs, setShowFaqs] = useState(false);
    const [likedMessages, setLikedMessages] = useState({});
    const [showBubble, setShowBubble] = useState({})
    const [fetchingFaq, setFetchingFaq] = useState(false);
    const [sessionId, setSessionId] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    
    const messagesEndRef = useRef(null);

    const authState = useSelector((state) => state.authUser);
    const userEmail = authState?.profile?.email || "";
    const [country, setCountry] = useState("India");
    const user_id = authState?.profile?.user_id;
    const user_email = authState?.profile?.email;

    useEffect(() => {
        const id = crypto.randomUUID();
        setSessionId(id);
    }, []);

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const request = { resource: `api/zoho/getAllEmployees/${userEmail}` };
                const response = await api.ZohoService.getAllEmployees(request);
                if (response?.data?.country) {
                    setCountry(response.data.country);
                }
            } catch (error) {
                console.error("Error fetching user details:", error);
            }
        };
        fetchUserDetails();
    }, [userEmail]);

    useEffect(() => {
        fetchFaqs();
    }, []);

    const getCurrentTime = () => {
        const now = new Date();
        return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
    };

    const fetchFaqs = async () => {
        try {
            setFetchingFaq(true);
            const data = await api.chatbot.getFAQs();

            if (data?.status === 200 && Array.isArray(data.data.top_faqs)) {
                setFaqs(data.data.top_faqs);
                setShowFaqs(true);
            } else {
                setFaqs([]);
            }
        } catch (error) {
            console.error("FAQ Fetch Error:", error);
            setFaqs([]);
        } finally {
            setFetchingFaq(false);
        }
    };

    const handleFaqClick = async (question) => {
        setQuery("");
        const newMessages = [...messages, { text: question, sender: "user", time: getCurrentTime() }];
        setIsTyping(true);
        setMessages(newMessages);
        setLoading(true);

        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);

        try {
            const requestBody = {
                string_to_search: question,
                email_id: userEmail,
                collection_name: country,
                session_id: sessionId,
            };
            const response = await api.chatbot.getQuestionResponse(requestBody);

            let botResponse = "Sorry, I couldn't fetch the answer.";
            if (response?.status === 200 && response?.data?.response) {
                botResponse = response.data.response
                    .replace(/(\d+)\./g, '\n$1.')
                    .replace(/\n+/g, '\n');
                trackEvent('hr_bot_faq_query_raised', 'HR Bot FAQ Query Raised', 'HR Bot FAQ Query Raised', 1, user_id, user_email);
            }
            setIsTyping(true);
            setMessages((prev) => [
                ...prev,
                { text: botResponse, sender: "bot", time: getCurrentTime(), showFeedback: true }
            ]);

            // Scroll again after bot response
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100);
        } catch (error) {
            console.error("API Error:", error);
            setIsTyping(true);
            setMessages((prev) => [
                ...prev,
                { text: "Something went wrong. Please try again.", sender: "bot", time: getCurrentTime(), showFeedback: true }
            ]);

            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenChat = () => {
        setShow(true);
        setShowBubble(false);
        trackEvent('hr_bot_viewed', 'HR Bot Viewed', 'HR Bot Viewed', 1, user_id, user_email);
    };

    const handleSend = async () => {
        if (!query.trim()) return;

        const newMessages = [...messages, { text: query, sender: "user", time: getCurrentTime() }];
        setIsTyping(true);
        setMessages(newMessages);
        setQuery("");
        setLoading(true);

        try {
            const requestBody = {
                string_to_search: query,
                email_id: userEmail,
                collection_name: country,
                session_id: sessionId
            };

            const response = await api.chatbot.getQuestionResponse(requestBody);

            let botResponse = "Sorry, I couldn't fetch the answer.";
            if (response?.status === 200 && response?.data?.response) {
                botResponse = response.data.response
                    .replace(/(\d+)\./g, '\n$1.')
                    .replace(/\n+/g, '\n');

                trackEvent('hr_bot_user_query_raised', 'HR Bot User Query Raised', 'HR Bot User Query Raised', 1, user_id, user_email);

            }
            setIsTyping(true);
            setMessages((prev) => [
                ...prev,
                { text: botResponse, sender: "bot", time: getCurrentTime(), showFeedback: true }
            ]);
        } catch (error) {
            console.error("API Error:", error);
            setIsTyping(true);
            setMessages((prev) => [
                ...prev,
                { text: "Something went wrong. Please try again.", sender: "bot", time: getCurrentTime(), showFeedback: true }
            ]);
        } finally {
            setLoading(false);
        }
    };


    const handleLike = (index) => {
        setLikedMessages((prev) => ({ ...prev, [index]: "liked" }));
        setIsTyping(true);
        setMessages((prev) =>
            prev.map((msg, i) =>
                i === index ? { ...msg, showFeedback: false } : msg
            )
        );
        setIsTyping(true);
        setMessages((prev) => [
            ...prev,
            { text: "Glad I could help! Feel free to reach out if you need anything else. Have a great day!", sender: "bot", time: getCurrentTime(), showFeedback: false }
        ]);
    };

    const handleUnlike = (index) => {
        setLikedMessages((prev) => ({ ...prev, [index]: "unliked" }));
        setIsTyping(true);
        setMessages((prev) =>
            prev.map((msg, i) =>
                i === index ? { ...msg, showFeedback: false } : msg
            )
        );
        setIsTyping(true);
        setMessages((prev) => [
            ...prev,
            {
                text: "Sorry about that! Please share more details and I'll do my best to clarify or get in touch with hr@agilisium.com for further information.",
                sender: "bot",
                time: getCurrentTime(),
                showFeedback: false
            }

        ]);
    };

    useEffect(() => {
        const showBubble = () => {
            setShowBubble(true);

            setTimeout(() => {
                setShowBubble(false);
            }, 5000);
        };

        setTimeout(showBubble, 1000);

        const interval = setInterval(showBubble, 10000);

        return () => clearInterval(interval);
    }, []);


    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    return (
        <>
            <div className={Styles.botContainer}>

                {!show && showBubble && (
    <div
        style={{
            position: "fixed",
            bottom: "85px",
            left: "55px",
            backgroundColor: "#fff",
            color: "#333",
            padding: "12px 16px",
            borderRadius: "18px",
            fontSize: "14px",
            fontWeight: 600,
            width: "225px",
            textAlign: "left",
            lineHeight: "1.2",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
            animation: "fadeIn 0.3s ease-in-out",
            zIndex: 99999,
        }}
    >
        Ask your HR Virtual Agent!
        <div
            style={{
                position: "absolute",
                bottom: "-10px",
                left: "40px",
                width: 0,
                height: 0,
                borderLeft: "10px solid transparent",
                borderRight: "10px solid transparent",
                borderTop: "10px solid #fff",
            }}
        />
    </div>
)}


                {!show && <button
                    className={Styles.chatButton}
                    onClick={handleOpenChat}
                    aria-label="Open chatbot"
                >
                    <img src={robot} alt="Chatbot" className={Styles.botIcon} />
                </button>}
            </div>

            <div className={`${Styles.chatPanel} ${show ? Styles.show : ""}`} aria-hidden={!show} style={{ zIndex: '99999' }}>
                <div className={Styles.header}>
                    <h5>#askAGSbot</h5>
                    <button className={Styles.closeBtn} onClick={() => {
                        setShow(false);
                        setIsTyping(false);
                        setMessages([]);
                        setLikedMessages({});
                    }}>
                        <IoClose size={20} color="white" />
                    </button>

                </div>

                <div className={Styles.body} style={{ overflow: 'auto' }}>
                    <h5 className={Styles.greetingText}>Most Frequently Asked Qustions</h5>
                    <div className="d-flex flex-wrap gap-2 border-bottom mb-2 pb-2">
                        {fetchingFaq ? <LoadingText text="Fetching FAQs" /> : <></>}
                        {faqs?.map((faq, index) => (
                            <div
                                key={index}
                                className={Styles.faqButton}
                                style={{ pointerEvents: loading ? 'none' : isTyping ? 'none' : 'auto' }}
                                onClick={() => handleFaqClick(faq.question)}
                            >
                                {faq.question}
                            </div>
                        ))}
                    </div>

                    <div className={Styles.chatMessages}>
                        {/* <h5 className={Styles.greetingText}>Hi there! How can I assist you today?</h5> */}

                        {messages?.map((msg, index) => (<>
                            <div className="d-flex gap-1 align-items-center">
                                {msg.sender === "bot" && <img src={robot} alt="robot" className={`${Styles.robotIconMsg}`} />}
                            </div>
                            <div className={`gap-1 ${msg.sender === "user" ? 'd-flex justify-content-end' : 'justify-content-start'}`}>
                                <div className={`${msg.sender === "user" ? 'd-flex flex-column align-items-end' : 'flex-row'}`}>
                                    {msg.sender === "user" && <div className="userIconMsg">You</div>}
                                    <div key={index} className={`${Styles.message} ${msg.sender === "user" ? Styles.user : Styles.bot}`}>
                                        <div>
                                            {msg.sender === "bot" && index === messages.length - 1 && !loading ? (
                                                <TypingMessage message={msg?.text} onDone={() => setIsTyping(false)} />
                                            ) : (
                                                <p style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</p>
                                            )}
                                        </div>

                                        {msg.sender === "bot" && msg.showFeedback && !likedMessages[index] && !isTyping && index === messages.length - 1 && (
                                            <div className={Styles.feedbackButtons}>
                                                <button onClick={() => handleLike(index)} className={Styles.likeBtn}>👍</button>
                                                <button onClick={() => handleUnlike(index)} className={Styles.unlikeBtn}>👎</button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                        ))}
                        {loading && <div className="d-flex gap-1 align-items-center">
                            <div className="d-flex gap-1 align-items-center">
                                <img src={robot} alt="robot" className={`${Styles.loadingRobot}`} />
                            </div>
                            <LoadingText text="Analyzing" />
                        </div>
                        }

                        <div ref={messagesEndRef} className="m-3 p-3" />
                    </div>

                    <div className={Styles.inputContainer}>
                        <input
                            type="text"
                            placeholder="Enter your query here"
                            disabled={loading || isTyping}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && query.trim() && !loading) {
                                    handleSend();
                                }
                            }}
                            className={Styles.input}
                        />

                        <button className={Styles.sendButton} onClick={handleSend} disabled={!query.trim() || loading} aria-label="Send message">
                            <BsSend size={20} />
                        </button>

                    </div>
                </div>
            </div>
        </>
    );
};

export default Chatbot;